import * as angular from 'angular';
import * as _ from 'lodash';

import { IRootScope } from '../../IRootScope';
import { toggleEditMode, refreshClick } from '../../common/events';
import { Shape, ContextualField, Miniature, IValueViewModel, ComponentDefinition, RefreshClickData, Dictionary } from '../../models';
import { IDashboardService, ILoggerService, IDateRangeService, IStoreService } from '../../services';
import { IWidget } from '../../widgets';
import { editWidgetState } from '../../common';
import { shapeControllerCommon as common } from './editShapes.common';
import { Store, StoreElement } from '../../stateManagement';

class EditShapesDashboardController implements angular.IComponentController {
    canBind: boolean;
    isLoading: boolean;
    isEditMode: boolean;
    item: IWidget;
    fields: ContextualField[];
    formController: angular.IFormController;
    loadingMessage: string;
    miniatures: Miniature[];
    pointsFormController: angular.IFormController;
    selectedShape: Shape;
    viewModels: Dictionary<IValueViewModel>;
    viewModelList: IValueViewModel[];

    private _watchers: (() => void)[] = [];
    private _storeLoading: angular.IDeferred<void>;

    static $inject = [
        '$rootScope',
        '$state',
        '$stateParams',
        '$q',
        'storeService',
        'dashboardService',
        'loggerService',
        'dateRangeService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$stateParams: angular.ui.IStateParamsService,
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _dashboardService: IDashboardService,
        private _loggerService: ILoggerService,
        private _dateRangeService: IDateRangeService
    ) {
    }

    $onInit() {
        this.isEditMode = true;
        if (!this._$rootScope.isEditMode) {
            this._$rootScope.$broadcast(toggleEditMode);
        }
        this._dateRangeService.beginDate(this._$stateParams.beginDate);
        this._dateRangeService.endDate(this._$stateParams.endDate);
        this._dateRangeService.initialized(true);

        this.miniatures = common.miniatures;
        this.fields = common.getFields(
            () => this.selectedShape,
            () => this.item.configuration.shapes
        );

        // Connect to store
        // /!\ could not be done right for now with DASHBAORD as dashboard objects are not the same
        this.isLoading = true;
        this._storeLoading = this._$q.defer<void>();
        this._watchers.push(this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.ValueViewModels]
        ));

        // old fashionned loading
        this._storeLoading.promise.then(() => this.loadData());
    }

    private refreshStore(store: Store) {
        this.viewModels = store.valueViewModels.dictionary;
        this.viewModelList = _.values(this.viewModels);
        this._storeLoading.resolve();
    }

    private loadData() {
        this.isLoading = true;
        this._dashboardService
            .getDashboard(this._$stateParams.dashboardGuid)
            .then(dashboard => {
                this.canBind = !!dashboard.groupTypes && dashboard.groupTypes.length > 0;
                const item: any = _.find(dashboard.configuration.widgets, (w: IWidget) => w.guid === this._$stateParams.widgetGuid);
                if (!item.configuration.shapes) {
                    item.configuration.shapes = [];
                }
                this.item = item;
                if (this.item.configuration.shapes && this.item.configuration.shapes.length) {
                    this.selectedShape = this.item.configuration.shapes[0];
                }
                this._$rootScope.$broadcast(refreshClick, { beginDate: this._$stateParams.beginDate, endDate: this._$stateParams.endDate } as RefreshClickData);
            })
            .finally(() => this.isLoading = false);
    }

    // Destroy
    $onDestroy() {
        try {
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach(w => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    onDrop() {
        common.onDrop(this.item, newshape => this.selectedShape = newshape);
    }

    addPoint() {
        common.addPoint(this.selectedShape, this.canBind);
    }

    onSave() {
        this.loadingMessage = 'Saving';
        this.isLoading = true;
        const dashboardGuid = this._$stateParams.dashboardGuid;
        return this._dashboardService
            .updateWidget(dashboardGuid, this.item)
            .then((dashboard) => {
                this.item = _.find(dashboard.configuration.widgets, (w: IWidget) => w.guid === this.item.guid) as any;
                this._loggerService.logSuccess(`Widget ${this.item.name} saved successfully.`);
                if (this.selectedShape) {
                    this.selectedShape = _.find(this.item.configuration.shapes, s => s.guid === this.selectedShape.guid);
                }
                this.pointsFormController.$setPristine();
            }).catch(() => {
                this._loggerService.logError(`Error while saving ${this.item.name} .`);
            }).finally(() => this.isLoading = false);
    }

    onCancel() {
        this.pointsFormController.$setPristine();
        this._$state.go('dashboard', { dashboardName: this._$stateParams.dashboardGuid, beginDate: this._$stateParams.beginDate, endDate: this._$stateParams.endDate });
    }

    unselect($event: angular.IAngularEvent) {
        if (this.canSelect() && !$event.defaultPrevented) {
            this.selectedShape = null;
        }
    }
    
    onDelete(shapes: Shape[]) {
        this.onSave();
    }

    onDuplicate(shape: Shape) {
        this.formController.$getControls().forEach(c => c.$validate());
    }

    gotoSeries() {
        if (!this.canSelect()) {
            this._loggerService.logWarning(`Save or cancel changes before editing series.`);
        } else {
            this._$state.go(editWidgetState, this._$stateParams);
        }
    }

    canSelect() {
        return !(this.pointsFormController?.$dirty || this.formController?.$dirty)
            && !(this.pointsFormController?.$invalid || this.formController?.$invalid);
    }
}

export const wtEditShapesDashboard: ComponentDefinition = {
    componentId: 'wtEditShapesDashboard',
    controller: EditShapesDashboardController,
    template: require('./editShapes.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};